import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Phone, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { prenom: "", nom: "", telephone: "", email: "", code_postal: "" };

/**
 * Reusable "manual lead" creation modal.
 *
 * Props:
 *   open, onOpenChange
 *   onCreated(lead) — called after a successful creation
 *
 * Merchant selection:
 *   - admin: full list (search by name/zone)
 *   - licencie: scoped (backend returns only their merchants via /api/merchants)
 *   - commercant: no selector — backend overrides merchant_id
 */
export function ManualLeadModal({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const role = user?.role;
  const showMerchantSelector = role !== "commercant";

  const [form, setForm] = useState(EMPTY);
  const [merchantId, setMerchantId] = useState("");
  const [source, setSource] = useState("phone_call");
  const [merchants, setMerchants] = useState([]);
  const [search, setSearch] = useState("");
  const [consentDataSharing, setConsentDataSharing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY); setMerchantId(""); setSource("phone_call"); setSearch(""); setConsentDataSharing(false); setSubmitting(false);
      return;
    }
    if (showMerchantSelector) {
      api.get("/merchants").then(({ data }) => setMerchants(data || [])).catch(() => {});
    }
  }, [open, showMerchantSelector]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return merchants;
    return merchants.filter((m) =>
      (m.nom || "").toLowerCase().includes(q) ||
      (m.ville || "").toLowerCase().includes(q) ||
      (m.zone || "").toLowerCase().includes(q),
    );
  }, [merchants, search]);

  const canSubmit =
    form.prenom.trim() && form.nom.trim() && form.telephone.trim() &&
    form.email.trim() && form.code_postal.trim() &&
    consentDataSharing &&
    (!showMerchantSelector || merchantId);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        prenom: form.prenom.trim(),
        nom: form.nom.trim(),
        telephone: form.telephone.trim(),
        email: form.email.trim(),
        code_postal: form.code_postal.trim(),
        merchant_id: showMerchantSelector ? merchantId : "auto",
        source,
        consent_data_sharing: true,
      };
      const { data } = await api.post("/leads/manual", payload);
      toast.success(`Lead ${data.prenom} ${data.nom} créé`);
      onOpenChange(false);
      if (onCreated) onCreated(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.warning("Un lead avec cet email ou téléphone existe déjà");
      } else {
        toast.error(formatApiError(err));
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manual-lead-modal" className="max-w-lg">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center mb-2">
            <Phone className="w-6 h-6 text-[#1B4FA6]" />
          </div>
          <DialogTitle className="font-display text-xl text-[#1F2937]">
            Nouveau lead (appel direct)
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] leading-relaxed pt-1">
            Enregistrez un client qui a appelé directement. Il recevra le même email de bienvenue qu'un lead via QR code.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="manual-lead-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ml-prenom">Prénom</Label>
              <Input id="ml-prenom" data-testid="ml-prenom" value={form.prenom} onChange={handleChange("prenom")} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ml-nom">Nom</Label>
              <Input id="ml-nom" data-testid="ml-nom" value={form.nom} onChange={handleChange("nom")} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ml-tel">Téléphone</Label>
              <Input id="ml-tel" data-testid="ml-tel" value={form.telephone} onChange={handleChange("telephone")} required placeholder="06..." />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ml-cp">Code postal</Label>
              <Input id="ml-cp" data-testid="ml-cp" value={form.code_postal} onChange={handleChange("code_postal")} required maxLength={5} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ml-email">Email</Label>
            <Input id="ml-email" data-testid="ml-email" type="email" value={form.email} onChange={handleChange("email")} required />
          </div>

          {showMerchantSelector && (
            <div className="space-y-1.5">
              <Label>Commerçant</Label>
              <Input
                placeholder="Rechercher par nom, ville, zone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="ml-merchant-search"
                className="mb-2"
              />
              <Select value={merchantId} onValueChange={setMerchantId}>
                <SelectTrigger data-testid="ml-merchant-select">
                  <SelectValue placeholder="Choisir un commerçant…" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {filtered.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-[#939BA0]">Aucun commerçant</div>
                  ) : filtered.map((m) => (
                    <SelectItem key={m.id} value={m.id} data-testid={`ml-merchant-opt-${m.id}`}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{m.nom}</span>
                        <span className="text-xs text-[#939BA0]">{m.ville || "—"} · {m.zone || "siège"}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Canal</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="ml-source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="phone_call" data-testid="ml-source-phone">
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#1B4FA6]" /> Appel téléphonique</div>
                </SelectItem>
                <SelectItem value="manual" data-testid="ml-source-manual">
                  <div className="flex items-center gap-2"><Pencil className="w-4 h-4 text-[#1B4FA6]" /> Saisie manuelle (autre)</div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-start gap-3 pt-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={consentDataSharing}
              onChange={(e) => setConsentDataSharing(e.target.checked)}
              data-testid="ml-consent-data-sharing"
              className="mt-1 w-[18px] h-[18px] accent-[#1B4FA6]"
            />
            <span className="text-[12px] text-[#4B5563] leading-[1.45]">
              <strong className="text-[#1F2937]">J'atteste que le client a donné son accord</strong> pour la transmission de ses coordonnées (nom, prénom, téléphone, email) au commerçant partenaire, dans le cadre de l'opération "30&nbsp;€ offerts". <strong className="text-[#1F2937]">(obligatoire)</strong>
            </span>
          </label>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="ml-cancel" className="rounded-lg">
              Annuler
            </Button>
            <Button type="submit" disabled={!canSubmit || submitting} data-testid="ml-submit"
              className="rounded-lg bg-[#1B4FA6] hover:bg-[#143C82] text-white font-semibold disabled:opacity-40">
              {submitting ? "Enregistrement…" : "Enregistrer le lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ManualLeadModal;
